import React, { useEffect, useMemo, useState } from "react";
import { Check, Lock } from "lucide-react";
import { Helmet, GameMessage } from "../games/GameParts";
import {
  conferencesOf, formatEasternDay, formatEasternTime, isRowLocked, matchupLabel, SORTS, sortPickRows, spreadLabel,
} from "../games/pickThree";
import { PickThreeBackdrop, Slots } from "./PickThreeParts";

// The picker, shared by the signed-in page and the signed-out one: search,
// three sorts, one row per FBS team, and the bar of three slots that stays at
// the bottom of the screen. The two pages differ only in what the bar's button
// does, so that is passed in as children.
//
// Teams lock one by one through the week, so both pages hold a clock
// (useLockClock) and pass `now` down: a row greys out and a pick gains its lock
// at the moment the team locks, without a reload. The server enforces the same
// rule, so a stale screen can only fail a save, never sneak one through.

/** The current time, ticking every 30 seconds, for lock checks on screen. */
export function useLockClock(intervalMs = 30_000) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const tick = () => setNow(new Date());
    const id = setInterval(tick, intervalMs);
    document.addEventListener("visibilitychange", tick);
    return () => { clearInterval(id); document.removeEventListener("visibilitychange", tick); };
  }, [intervalMs]);
  return now;
}

const LABEL = { fontSize: 11, fontWeight: 800, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--color-text-faint)", margin: 0, padding: "8px 12px 4px", backgroundColor: "var(--color-bg-page)" };

function PickRow({ row, selected, full, locked, onToggle }) {
  // A locked team can be neither added nor removed; a full set takes no more.
  const disabled = locked || (full && !selected);
  const when = locked ? "locked" : row.bye ? null : row.tbd ? "time to be confirmed" : formatEasternTime(row.kickoff);
  return (
    <button
      type="button"
      onClick={() => onToggle(row.slug)}
      disabled={disabled}
      aria-pressed={selected}
      style={{
        display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", border: "none",
        borderBottom: "1px solid var(--color-border)", textAlign: "left", font: "inherit", cursor: disabled ? "default" : "pointer",
        backgroundColor: selected ? "var(--color-band-bg)" : "var(--color-bg-card)", opacity: (disabled && !selected) || row.bye ? 0.55 : 1,
      }}
    >
      <Helmet team={row} size={34} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: "block", fontSize: 15, fontWeight: 700, color: "var(--color-text-strong)" }}>{row.name}</span>
        <span style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginTop: 1 }}>
          {matchupLabel(row)}{when ? `, ${when}` : ""}
        </span>
        <FormLine row={row} />
      </span>
      {(() => {
        const spread = spreadLabel(row);
        const none = spread === "No line";
        return (
          <span style={{ minWidth: 48, textAlign: "right", fontSize: none ? 12 : 13, fontWeight: none ? 500 : 700, color: none ? "var(--color-text-faint)" : "var(--color-text-strong)", fontVariantNumeric: "tabular-nums" }}>
            {spread}
          </span>
        );
      })()}
      <span
        aria-hidden="true"
        style={{
          width: 22, height: 22, flexShrink: 0, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
          border: selected ? "none" : "1.5px solid var(--color-border)", backgroundColor: selected ? "var(--color-cta)" : "transparent", color: "#fff",
        }}
      >
        {selected && (locked ? <Lock size={12} strokeWidth={3} /> : <Check size={14} strokeWidth={3} />)}
      </span>
    </button>
  );
}

const pointsColor = (n) => (n > 0 ? "var(--color-success)" : n < 0 ? "var(--color-danger)" : "var(--color-text-faint)");

// "Last week +13 · Avg +8.4": how the team has been scoring, beside the spread
// it is being picked on. Nothing in week 1, when there is no last week.
function FormLine({ row }) {
  if (!hasForm(row)) return null;
  return (
    <span style={{ display: "block", fontSize: 12, color: "var(--color-text-muted)", marginTop: 2, fontVariantNumeric: "tabular-nums" }}>
      Last week{" "}
      {row.lastWeek == null
        ? <b style={{ fontWeight: 700, color: "var(--color-text-faint)" }}>Bye</b>
        : <b style={{ fontWeight: 800, color: pointsColor(row.lastWeek) }}>{signedPoints(row.lastWeek)}</b>}
      {row.seasonAvg != null && (
        <>
          {" \u00b7 Avg "}
          <b style={{ fontWeight: 800, color: pointsColor(row.seasonAvg) }}>{signedPoints(row.seasonAvg)}</b>
        </>
      )}
    </span>
  );
}

const hasForm = (row) => row.formWeek >= 2;
const signedPoints = (n) => (n > 0 ? `+${n}` : n < 0 ? `\u2212${-n}` : "0");

/**
 * Search, sorts and the rows. `selected` is an array of slugs, at most three.
 * `now` decides which teams have locked (useLockClock).
 */
export function PickList({ rows, selected, onToggle, now }) {
  const [sort, setSort] = useState("kickoff");
  const [query, setQuery] = useState("");
  const [conference, setConference] = useState("");

  // The filter shows only when the rows carry conferences: the signed-out slate
  // does from migration 086 on.
  const conferences = useMemo(() => conferencesOf(rows), [rows]);
  const ordered = useMemo(() => sortPickRows(rows, sort), [rows, sort]);
  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ordered.filter((row) => (!q || row.name.toLowerCase().includes(q)) && (!conference || row.conference === conference));
  }, [ordered, query, conference]);
  const full = selected.length === 3;

  // Day headings, only when ordered by kickoff.
  let lastDay = null;
  return (
    <>
      <input
        type="search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search teams"
        aria-label="Search teams"
        style={{ width: "100%", boxSizing: "border-box", padding: "10px 12px", borderRadius: 10, border: "none", backgroundColor: "var(--color-bg-subtle)", fontSize: 16, color: "var(--color-text-strong)" }}
      />
      {conferences.length > 1 && (
        <select
          value={conference}
          onChange={(e) => setConference(e.target.value)}
          aria-label="Filter by conference"
          style={{
            width: "100%", boxSizing: "border-box", marginTop: 8, padding: "10px 12px", borderRadius: 10, border: "none",
            backgroundColor: "var(--color-bg-subtle)", fontSize: 16, color: conference ? "var(--color-text-strong)" : "var(--color-text-muted)",
          }}
        >
          <option value="">All conferences</option>
          {conferences.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
      )}
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", margin: "10px 0" }}>
        {/* No Last week sort in week 1, when there is nothing to sort by. */}
        {SORTS.filter((s) => s.id !== "lastWeek" || rows.some(hasForm)).map((s) => (
          <button
            key={s.id}
            type="button"
            onClick={() => setSort(s.id)}
            aria-pressed={sort === s.id}
            style={{
              padding: "6px 11px", borderRadius: 999, border: "none", fontSize: 12, fontWeight: 700, cursor: "pointer",
              // The button tokens, not text-strong: text-strong turns white in
              // dark mode, which made the selected pill white on white.
              backgroundColor: sort === s.id ? "var(--color-cta)" : "var(--color-bg-subtle)", color: sort === s.id ? "var(--color-cta-text)" : "var(--color-text-muted)",
            }}
          >
            {s.label}
          </button>
        ))}
      </div>
      <div style={{ margin: "0 -16px", borderTop: "1px solid var(--color-border)" }}>
        {shown.map((row) => {
          const day = sort === "kickoff" ? (row.bye ? "Bye week" : formatEasternDay(row.kickoff)) : null;
          const heading = day && day !== lastDay ? day : null;
          if (day) lastDay = day;
          return (
            <React.Fragment key={row.slug}>
              {heading && <div style={LABEL}>{heading}</div>}
              <PickRow row={row} selected={selected.includes(row.slug)} full={full} locked={isRowLocked(row, now)} onToggle={onToggle} />
            </React.Fragment>
          );
        })}
        {shown.length === 0 && <div style={{ padding: 16 }}><GameMessage>No team matches that search and conference.</GameMessage></div>}
      </div>
    </>
  );
}

/**
 * The three picks, fixed to the bottom of the screen while the list scrolls.
 * `teams` is a slug-keyed map with name and logo; `children` is the button and
 * any note under the slots. `locked`, `scores`, `captain`, `onCaptain`,
 * `canCaptain` and `onRemove` pass through to Slots.
 */
export function PickBar({ selected, teams, children, scores = null, locked = [], captain = null, onCaptain, canCaptain, onRemove }) {
  return (
    <>
      <div
        style={{
          position: "fixed", left: 0, right: 0, bottom: 0, zIndex: 20, borderTop: "1px solid var(--color-border)",
          backgroundColor: "var(--color-chrome)", backdropFilter: "blur(12px)", WebkitBackdropFilter: "blur(12px)",
          paddingBottom: "env(safe-area-inset-bottom)",
        }}
      >
        <div style={{ maxWidth: 600, margin: "0 auto", padding: "10px 16px 12px" }}>
          <PickThreeBackdrop style={{ borderRadius: 14, padding: 6 }}>
            <Slots
              picks={selected} teams={teams} compact scores={scores} locked={locked}
              captain={captain} onCaptain={onCaptain} canCaptain={canCaptain} onRemove={onRemove}
            />
          </PickThreeBackdrop>
          {children}
        </div>
      </div>
      {/* Room for the bar, so the last rows are not hidden under it. */}
      <div style={{ height: 150 }} />
    </>
  );
}
